import React, { useCallback, useMemo, useState } from 'react';
import {
  ClickAwayListener,
  Paper,
  PaperProps,
  TextField,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { RichTreeView, treeItemClasses } from '@mui/x-tree-view';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDownIcon from '@material-ui/icons/ArrowDropDown';
import { CustomTreeItem, TreePopoverContent } from './styles';
import { TreeViewSelectNode, TreeViewSelectProps } from './types';
import {
  getSelectableOptions,
  filterToSelectableIds,
  expandSelectionWithParents,
  toggleNodeSelection,
} from './utils';

interface TreeViewSelectPaperProps extends PaperProps {
  items: TreeViewSelectNode[];
  selectedItems: string[];
  expandedItems: string[];
  onSelectionChange: (
    event: React.SyntheticEvent | null,
    selectedIds: string[],
  ) => void;
  onExpandedItemsChange: (
    event: React.SyntheticEvent | null,
    itemIds: string[],
  ) => void;
  onItemClick: (event: React.MouseEvent, itemId: string) => void;
}

function TreeViewSelectPaper({
  children,
  items,
  selectedItems,
  expandedItems,
  onSelectionChange,
  onExpandedItemsChange,
  onItemClick,
  style,
  ...other
}: TreeViewSelectPaperProps) {
  return (
    <Paper
      {...other}
      style={{
        ...style,
        width: style?.width,
        maxWidth: style?.width,
        overflow: 'hidden',
      }}
      onMouseDown={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <TreePopoverContent>
        <RichTreeView
          checkboxSelection
          multiSelect
          items={items}
          selectedItems={selectedItems}
          expandedItems={expandedItems}
          onSelectedItemsChange={onSelectionChange}
          onExpandedItemsChange={onExpandedItemsChange}
          onItemClick={onItemClick}
          slots={{
            item: CustomTreeItem,
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDownIcon,
          }}
          getItemLabel={(item: TreeViewSelectNode) => item.label}
          selectionPropagation={{
            descendants: true,
            parents: true,
          }}
        />
      </TreePopoverContent>
      <div style={{ display: 'none' }}>{children}</div>
    </Paper>
  );
}

export function TreeViewSelect({
  items,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  error,
  helperText,
  id,
  disabled = false,
}: TreeViewSelectProps) {
  const [open, setOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const options = useMemo(() => getSelectableOptions(items), [items]);
  const selectedOptions = useMemo(
    () => options.filter(option => value.includes(option.id)),
    [options, value],
  );
  const selectedItems = useMemo(
    () => expandSelectionWithParents(value, items),
    [value, items],
  );

  const handleSelectionChange = useCallback(
    (_: React.SyntheticEvent | null, selectedIds: string[]) => {
      onChange(filterToSelectableIds(selectedIds, items));
    },
    [onChange, items],
  );

  const handleExpandedItemsChange = useCallback(
    (_: React.SyntheticEvent | null, itemIds: string[]) => {
      setExpandedItems(itemIds);
    },
    [],
  );

  const handleItemClick = useCallback(
    (event: React.MouseEvent, itemId: string) => {
      const target = event.target as HTMLElement;
      if (
        target.closest('input[type="checkbox"]') ||
        target.closest('.MuiCheckbox-root')
      ) {
        return;
      }
      if (target.closest(`.${treeItemClasses.iconContainer}`)) return;

      onChange(toggleNodeSelection(itemId, value, items));
    },
    [onChange, value, items],
  );

  const PaperComponent = useCallback(
    (props: PaperProps) => (
      <TreeViewSelectPaper
        {...props}
        items={items}
        selectedItems={selectedItems}
        expandedItems={expandedItems}
        onSelectionChange={handleSelectionChange}
        onExpandedItemsChange={handleExpandedItemsChange}
        onItemClick={handleItemClick}
      />
    ),
    [
      items,
      selectedItems,
      expandedItems,
      handleSelectionChange,
      handleExpandedItemsChange,
      handleItemClick,
    ],
  );

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) setOpen(false);
      }}
    >
      <div>
        <Autocomplete
          multiple
          fullWidth
          disablePortal
          disabled={disabled}
          id={id}
          options={options}
          getOptionLabel={option => option.label}
          filterSelectedOptions
          value={selectedOptions}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(_, reason) => {
            if (reason === 'blur') return;
            setOpen(false);
          }}
          disableCloseOnSelect
          onChange={(_, selected) => {
            onChange(selected.map(option => option.id));
          }}
          getOptionSelected={(option, selected) => option.id === selected.id}
          PaperComponent={PaperComponent}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.label}
                color="primary"
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={label}
              placeholder={!value.length ? placeholder : ''}
              required={required}
              InputLabelProps={{ shrink: true }}
              error={error}
              helperText={helperText}
            />
          )}
        />
      </div>
    </ClickAwayListener>
  );
}
